import React from "react";
import { Box } from "@mui/material";

/**
 * The Tradeloft mark — two cards lifting, with the chevron that gives the name
 * its "loft".
 *
 * Flat fills and no gradient inside the mark itself, so the same geometry
 * survives from a 16px favicon to a 512px Play icon. Purely presentational:
 * this is the product's wordmark, not the Android package id
 * (`com.cardonomer.app`) or the OAuth deep-link scheme, both of which are
 * identifiers that must never be "rebranded".
 */
export const TradeloftGlyph = ({ size = 26, title }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    role={title ? "img" : "presentation"}
    aria-hidden={title ? undefined : "true"}
    focusable="false"
  >
    {title && <title>{title}</title>}
    <rect
      x="5" y="8" width="14" height="19" rx="3"
      transform="rotate(-10 12 17.5)"
      fill="#c4b5fd"
    />
    <rect
      x="13" y="4" width="14" height="19" rx="3"
      transform="rotate(8 20 13.5)"
      fill="#7c3aed"
    />
    <path
      d="M17.5 13.5 20.5 10.5 23.5 13.5"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/**
 * The wordmark, written "Tradeloft" as everywhere else, with the accent on
 * "loft" only. It is the one brand mark across the app (login, sign-up,
 * footer, Trade Arena); the public trade-list page draws the same glyph and
 * wordmark in server-rendered HTML (server/publicTradeList/renderPage.js).
 *
 * The kicker underneath names the place ("Trade Arena") and nothing else. It
 * used to carry a "· Live" status, which was never true: nothing in the Arena
 * is live, the other collector sees nothing until the offer is sent, and the
 * product decision is to make no "live" claims (B-25).
 */
const TradeloftMark = ({ kicker, size = 26, wordmarkSize = 17, accent = "#c084fc" }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
    <TradeloftGlyph size={size} />
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          fontSize: `${wordmarkSize}px`,
          fontWeight: 900,
          letterSpacing: "-0.03em",
          lineHeight: 1.1,
          color: "inherit",
        }}
      >
        Trade<Box component="span" sx={{ color: accent }}>loft</Box>
      </Box>
      {kicker && (
        <Box
          sx={{
            fontSize: "9px",
            fontWeight: 700,
            color: accent,
            textTransform: "uppercase",
            letterSpacing: "0.22em",
            lineHeight: 1.4,
            whiteSpace: "nowrap",
          }}
        >
          {kicker}
        </Box>
      )}
    </Box>
  </Box>
);

export default TradeloftMark;
